import { Icon, type IconProps } from "@minket/brand/icons/icon";
import { SearchIcon } from "@minket/brand/icons/search";
import { Button } from "@minket/ui/components/button";
import { focusRing } from "@minket/ui/lib/styles";
import { cn } from "@minket/ui/lib/utils";
import { Link, useRouter } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense, useCallback, useEffect, useRef, useState } from "react";
import type { Membership, SessionUser } from "@/lib/session";
import { useWorkspace, WorkspaceProvider } from "@/lib/workspace";
import { CommandPalette, MobileMenu, prefetchMenus, prefetchPalette } from "./deferred";
import { SidebarContent } from "./sidebar";
import { threadRight, threadUnder } from "./thread";
import { Wordmark } from "./wordmark";

/**
 * Menu, drawn on the brand icons' grid (20 px, a 1.5 px round stroke) so the shell's first load
 * carries no icon library.
 */
function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 5.75h13M3.5 10h13M3.5 14.25h13" />
    </Icon>
  );
}

const loadSetup = () => import("@/components/onboarding/setup-tile");
const SetupTile = lazy(() => loadSetup().then((m) => ({ default: m.SetupTile })));
const SetupPill = lazy(() => loadSetup().then((m) => ({ default: m.SetupPill })));

/**
 * Whether to load the setup tile: once the page has rendered, for an organization. The tile
 * decides the rest (the role, the stored progress, the setup flow in view), so a page never
 * waits for it.
 */
function useSetupWanted(): boolean {
  const { organization } = useWorkspace();
  const [rendered, setRendered] = useState(false);
  useEffect(() => setRendered(true), []);
  return rendered && organization !== null;
}

const SuspensionNotice = lazy(() =>
  import("./suspension.ondemand").then((module) => ({ default: module.SuspensionNotice })),
);

function Setup({ pill = false }: { pill?: boolean }) {
  const wanted = useSetupWanted();
  if (!wanted) return null;
  return <Suspense fallback={null}>{pill ? <SetupPill /> : <SetupTile />}</Suspense>;
}

/**
 * ⌘K (Ctrl K elsewhere) toggles the palette from anywhere in the console. The palette's code
 * starts loading as soon as the modifier goes down, so it is usually there by the K.
 */
function usePaletteShortcut(toggle: () => void) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Meta" || event.key === "Control") prefetchPalette();
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);
}

/**
 * After a navigation, focus moves to `<main>` so keyboard and screen reader users start on
 * the new page, as they would after a full load. Focus that is still on something inside the
 * page (a settings tab, a setup step) stays where it is.
 */
function useFocusMainOnNavigate() {
  const router = useRouter();
  useEffect(
    () =>
      router.subscribe("onResolved", (event) => {
        if (!event.fromLocation || !event.pathChanged) return;
        window.requestAnimationFrame(() => {
          const main = document.getElementById("main");
          const active = document.activeElement;
          if (!main) return;
          if (active && active !== document.body && active.isConnected && main.contains(active)) {
            return;
          }
          main.focus({ preventScroll: true });
        });
      }),
    [router],
  );
}

/**
 * The palette and the phone menu open without a Radix trigger (a shortcut, the phone bar), so
 * Radix has nothing to give focus back to when they close. This remembers what had focus when
 * one opened and returns focus there, or to the page when that element has gone.
 */
function useReturnFocus() {
  const opener = useRef<HTMLElement | null>(null);
  const remember = useCallback(() => {
    const active = document.activeElement;
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, []);
  const restore = useCallback((event: Event) => {
    event.preventDefault();
    const target = opener.current?.isConnected ? opener.current : document.getElementById("main");
    target?.focus({ preventScroll: true });
  }, []);
  return { remember, restore };
}

/**
 * The console frame (direction §7): a skip link, the paper rail with the thread down its right
 * edge in plate ink (from 1024 px; below it a 56 px paper bar with the thread under it, and the
 * same rail in a sheet behind Menu), the page, the setup tile while setup has steps left, and
 * the command palette. Every page renders inside `<main id="main">`, at most 80 rem wide, with
 * 16 px gutters on phones and 32 px from `md`; its foot keeps clear of the setup tile.
 */
export function Shell({
  user,
  memberships,
  children,
}: {
  user: SessionUser;
  memberships: readonly Membership[];
  children: ReactNode;
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteWanted, setPaletteWanted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWanted, setMenuWanted] = useState(false);
  const paletteFocus = useReturnFocus();
  const menuFocus = useReturnFocus();
  const { remember: rememberPaletteOpener } = paletteFocus;
  const openPalette = useCallback(() => {
    rememberPaletteOpener();
    setPaletteWanted(true);
    setPaletteOpen(true);
  }, [rememberPaletteOpener]);
  const togglePalette = useCallback(() => {
    setPaletteWanted(true);
    setPaletteOpen((open) => {
      if (!open) rememberPaletteOpener();
      return !open;
    });
  }, [rememberPaletteOpener]);
  const { remember: rememberMenuOpener } = menuFocus;
  const openMenu = useCallback(() => {
    rememberMenuOpener();
    setMenuWanted(true);
    setMenuOpen(true);
  }, [rememberMenuOpener]);
  usePaletteShortcut(togglePalette);
  useFocusMainOnNavigate();

  return (
    <WorkspaceProvider user={user} memberships={memberships}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-60 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:outline-none"
      >
        Skip to content
      </a>
      <div className="flex min-h-dvh [--thread:var(--seal-ink)]">
        <aside data-slot="rail" className="hidden w-[248px] shrink-0 lg:block">
          <div
            className={cn(
              "sticky top-0 h-dvh border-r border-sidebar-border bg-sidebar text-sidebar-foreground",
              threadRight,
            )}
          >
            <SidebarContent onOpenPalette={openPalette} />
          </div>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <header
            data-slot="phone-bar"
            className={cn(
              "sticky top-0 z-40 flex min-h-14 items-center gap-3 bg-background px-4 md:px-8 lg:hidden",
              threadUnder,
            )}
          >
            <Button
              variant="outline"
              size="sm"
              className="max-[419px]:px-2"
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? "mobile-menu" : undefined}
              onClick={openMenu}
              onPointerEnter={prefetchMenus}
              onFocus={prefetchMenus}
            >
              <MenuIcon size={20} className="size-5" />
              <span className="max-[419px]:sr-only">Menu</span>
            </Button>
            <Link to="/" className={cn(focusRing, "inline-flex rounded-sm px-1 py-0.5")}>
              <Wordmark height={17} />
              <span className="sr-only">Minket overview</span>
            </Link>
            <div className="ml-auto flex min-w-0 items-center gap-2">
              <Setup pill />
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Search"
              onClick={openPalette}
              onPointerEnter={prefetchPalette}
              onFocus={prefetchPalette}
            >
              <SearchIcon size={20} className="size-5" />
            </Button>
          </header>
          <main id="main" tabIndex={-1} className="flex-1 outline-none">
            <div className="w-full px-4 pt-[18px] pb-10 md:px-8 lg:px-10 lg:pt-7 lg:pb-[max(3rem,calc(var(--setup-tile-block,0px)+1.5rem))]">
              <Suspense fallback={null}>
                <SuspensionNotice />
              </Suspense>
              {children}
            </div>
          </main>
        </div>
      </div>
      <Setup />
      {menuWanted ? (
        <Suspense fallback={null}>
          <MobileMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            onCloseAutoFocus={menuFocus.restore}
          >
            <SidebarContent
              onNavigate={() => setMenuOpen(false)}
              onOpenPalette={() => {
                setMenuOpen(false);
                openPalette();
              }}
            />
          </MobileMenu>
        </Suspense>
      ) : null}
      {paletteWanted ? (
        <Suspense fallback={null}>
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            onCloseAutoFocus={paletteFocus.restore}
          />
        </Suspense>
      ) : null}
    </WorkspaceProvider>
  );
}
